import { SourceList } from 'constants/data';

import {
  Button,
  DatePicker,
  Dropdown,
  ErrorContainer,
  InputField,
  LabelComponent,
  Stack,
  TimePicker,
  Tooltip
} from 'components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { FC, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getHealthTracker,
  postHealthTracker,
  postHealthTrackerCategory
} from 'store/healthTracker/healthTrackerActions';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { convertLocalToUTC } from 'utils/dateHelpers/dateHelpers';

import style from './healthTracker.module.scss';
dayjs.extend(isSameOrBefore);

interface Props {
  metricIdView?: number | string | null;
}

const HealthTrackerForm: FC<Props> = ({ metricIdView }) => {
  const { clientId } = useParams();
  const dispatch = useAppDispatch();
  const { healthTrackerMetricList, healthTrackerCategoryList } = useAppSelector(
    (state) => state.healthTracker
  );
  const { page, size, sorts } = healthTrackerMetricList;

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      metricId: '',
      source: '',
      value: null,
      entryDate: null,
      entryTime: null,
      time: null,
      direction: '',
      name: null,
      concerningValue: false
    },
    validationSchema: Yup.lazy((values) => {
      const commonFields = {
        source: Yup.string().required('Source is required.'),
        entryDate: Yup.string()
          .nullable()
          .required('Date is required.')
          .test('Date', 'Date cannot be in the future.', (date) => {
            if (!date) return true;
            const now = dayjs();
            return dayjs(date).isSameOrBefore(now, 'day');
          }),
        value: Yup.number()
          .typeError('Value must be a number.')
          .nullable()
          .required('Value is required.')
          .min(0, 'Value must be positive.')
          .test(
            'maxDigits',
            'Value must have at most 5 digits (e.g., 99999 or 99.999).',
            (value) => {
              const totalDigits = value ? value.toString().replace('.', '').length : 0;
              return (
                value == null ||
                (totalDigits <= 5 && /^[0-9]{1,5}(\.[0-9]{0,4})?$/.test(value.toString()))
              );
            }
          ),
        time: Yup.string().nullable()
      };

      const ownMetricFields = {
        direction: Yup.string().required('Direction is required.'),
        name: Yup.string().nullable().required('Name is required.')
      };

      return Yup.object().shape({
        metricId: Yup.string().required('Category is required.'),
        ...(values.metricId === 'MY_OWN_METRIC'
          ? { ...commonFields, ...ownMetricFields }
          : commonFields)
      });
    }),
    onSubmit: (values) => {
      if (clientId && values.metricId !== 'MY_OWN_METRIC') {
        dispatch(postHealthTracker({ data: values, clientId })).then(() => {
          if (metricIdView && metricIdView.toString() === values.metricId) {
            dispatch(
              getHealthTracker({
                clientId,
                metricId: metricIdView,
                page,
                size,
                sorts
              })
            );
          }
          formik.resetForm();
        });
      }
      if (values.metricId === 'MY_OWN_METRIC' && clientId) {
        dispatch(
          postHealthTrackerCategory({
            data: values,
            clientId
          })
        ).then(() => formik.resetForm());
      }
    }
  });

  const categoryList = useMemo(() => {
    const newList = healthTrackerCategoryList.filter((a) => (a.hasEntries && !a.code) || a.code);
    return [...newList, { id: 'MY_OWN_METRIC', name: 'My own metric' }];
  }, [healthTrackerCategoryList]);

  const metricList = [
    { id: 'DECREASE', name: 'Decrease' },
    { id: 'INCREASE', name: 'Increase' }
  ];

  const unitFiltered = useMemo(
    () =>
      (healthTrackerCategoryList?.length > 0 &&
        healthTrackerCategoryList.find((a) => a.id === Number(formik.values.metricId))?.unit) ||
      (!formik.values.metricId && 'unit'),
    [formik.values.metricId, healthTrackerCategoryList]
  );

  const MetricName = useMemo(
    () =>
      (healthTrackerCategoryList?.length > 0 &&
        healthTrackerCategoryList.find((a) => a.id === Number(formik.values.metricId))?.code) ||
      null,
    [formik.values.metricId, healthTrackerCategoryList]
  );

  useEffect(() => {
    if (!formik.values.value || !MetricName) {
      formik.setFieldValue('concerningValue', false);
      return;
    }

    const value = Number(formik.values.value);

    if (
      (MetricName === 'PHQ-9' && value >= 15) ||
      (MetricName === 'BLOOD_PRESSURE' && value > 180) ||
      (MetricName === 'BLOOD_GLUCOSE' && (value < 55 || value > 240))
    ) {
      formik.setFieldValue('concerningValue', true);
    } else {
      formik.setFieldValue('concerningValue', false);
    }
  }, [formik.values.value, formik.values.metricId]);

  return (
    <form onSubmit={formik.handleSubmit} data-test-id="health-tracker-form">
      <Stack
        width="100%"
        data-test-id="health-tracker-form-top"
        display="flex"
        flexDirection="row"
        gap="16px"
        mb="16px"
      >
        <Stack width="100%">
          <LabelComponent text="Category" mandatory={true} />
          <Dropdown
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            data={categoryList}
            label="Select"
            name="metricId"
            id="metricId"
            value={formik.values.metricId}
            withSearch
            fitWidth
            enableSorting
            withTooltip={13}
            fitWidthMore={20}
          />
          {formik.errors.metricId && formik.touched.metricId && (
            <ErrorContainer
              visible={formik.errors.metricId && formik.touched.metricId}
              name="metricId"
              errors={formik.errors}
            />
          )}
        </Stack>
        <Stack width="100%">
          <LabelComponent text="Date" mandatory={true} />
          <DatePicker
            classnamesProps={style.inputField}
            onChange={(date: string) => {
              formik.setFieldValue('entryDate', date).then(() => {
                formik.setFieldTouched('entryDate', true);
              });
              if (formik.values.time && date) {
                formik.setFieldValue(
                  'entryTime',
                  convertLocalToUTC(dayjs(date).toString(), formik.values.time)
                );
              }
            }}
            onBlur={() => formik.setFieldTouched('entryDate', true)}
            value={formik.values.entryDate}
            placeholder="YYYY-MM-DD"
            maxDate={dayjs()}
          />
          {formik.errors.entryDate && formik.touched.entryDate && (
            <ErrorContainer
              visible={formik.errors.entryDate && formik.touched.entryDate}
              name="entryDate"
              errors={formik.errors}
            />
          )}
        </Stack>
        <Stack width="100%">
          <LabelComponent text="Time" />
          <TimePicker
            classnamesProps={style.inputField}
            name="time"
            onChange={(time: string) => {
              if (time === 'Invalid Date') {
                formik.setFieldValue('entryTime', null);
                formik.setFieldValue('time', null).then(() => {
                  formik.setFieldTouched('time', true);
                });
              } else {
                formik.setFieldValue(
                  'entryTime',
                  convertLocalToUTC(
                    formik.values.entryDate
                      ? dayjs(formik.values.entryDate).toString()
                      : dayjs().toString(),
                    time
                  )
                );
                formik.setFieldValue('time', time).then(() => {
                  formik.setFieldTouched('time', true);
                });
              }
            }}
            onBlur={() => {
              formik.setFieldTouched('time');
            }}
            value={formik.values.time}
          />
          {formik.errors.time && formik.touched.time && (
            <ErrorContainer
              visible={formik.errors.time && formik.touched.time}
              name="time"
              errors={formik.errors}
            />
          )}
        </Stack>
        <Stack width="100%">
          <LabelComponent text="Value" mandatory={true} />
          <InputField
            classnamesProps={clsx(style.inputField, !unitFiltered && style.noUnitAdornment)}
            id="value"
            name="value"
            type="text"
            onChange={(event) => {
              let inputValue = event.target.value;
              const cursorPosition = event.target.selectionStart || 0;

              inputValue = inputValue.replace(',', '.');

              if (inputValue.includes('.') && inputValue.length > 6) {
                inputValue = inputValue.substring(0, 6);
              } else if (!inputValue.includes('.') && inputValue.length > 5) {
                inputValue = inputValue.substring(0, 5);
              }

              if (cursorPosition < inputValue.length) {
                const newChar = event.target.value[cursorPosition] || '';

                if (
                  !(inputValue[cursorPosition] === '.' && cursorPosition !== inputValue.length - 1)
                ) {
                  inputValue =
                    inputValue.substring(0, cursorPosition) +
                    newChar +
                    inputValue.substring(cursorPosition + 1);
                }
              }

              const newVal = inputValue.length > 5 && inputValue.substring(0, 5).includes('.');
              if (inputValue.length > 5 && !newVal && inputValue.endsWith('.')) {
                inputValue = inputValue.slice(0, -1);
              }

              const validValue = /^[0-9]{1,5}(\.[0-9]{0,4})?$/.test(inputValue);
              if (validValue || inputValue === '') {
                formik.setFieldValue('value', inputValue === '' ? undefined : inputValue);
              }

              setTimeout(() => {
                event.target.setSelectionRange(cursorPosition, cursorPosition);
              }, 0);
            }}
            onBlur={formik.handleBlur}
            value={formik.values.value || ''}
            endAdornment={
              unitFiltered &&
              ((unitFiltered.length > 7 && (
                <Tooltip
                  toolTipText={unitFiltered}
                  blackTooltip
                  styles={{ marginTop: '0 !important' }}
                >
                  <span className={style.unitAdornment}>{unitFiltered}</span>
                </Tooltip>
              )) || <span className={style.unitAdornment}>{unitFiltered}</span>)
            }
          />
          {formik.errors.value && formik.touched.value && (
            <ErrorContainer
              visible={formik.errors.value && formik.touched.value}
              name="value"
              errors={formik.errors}
            />
          )}
        </Stack>
        <Stack width="100%">
          <LabelComponent text="Source" mandatory={true} />
          <Dropdown
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            data={SourceList}
            label="Select"
            name="source"
            id="source"
            value={formik.values.source}
            fitWidth
            withTooltip={17}
            withSearch
          />
          {formik.errors.source && formik.touched.source && (
            <ErrorContainer
              visible={formik.errors.source && formik.touched.source}
              name="source"
              errors={formik.errors}
            />
          )}
        </Stack>
      </Stack>
      {formik.values.concerningValue && MetricName !== 'PHQ-9' && (
        <Stack data-test-id="health-tracker-form-warning" className={style.warningMessage}>
          <span className={clsx('material-icons-round')}>warning_amber</span> This is a concerning
          value. Please call your manager.
        </Stack>
      )}
      {formik.values.value && !formik.errors.value && MetricName === 'PHQ-9' && (
        <Stack data-test-id="health-tracker-form-warning" className={style.warningMessage}>
          <span className={clsx('material-icons-round')}>warning_amber</span> If the PHQ-9 score is
          15 or higher or your client had thoughts of hurting themself, call your manager.
        </Stack>
      )}
      {formik.values.metricId === 'MY_OWN_METRIC' && (
        <Stack width="100%" data-test-id="health-tracker-form-bottom" className={style.ownMetric}>
          <Stack display="flex" flexDirection="row" alignItems="center" gap="6px">
            <LabelComponent text="My Own Metric" mandatory={true} />
            <Tooltip
              placement="bottom"
              toolTipText={
                <Stack width="400px" display="flex">
                  <p style={{ fontSize: '14px' }}>
                    Create your own metric here!
                    <br />
                    <br />
                    Decide on something you’d like to track with your client. It could be an action
                    like “walks around the block” or a metric like “resting heart rate.”
                    <br />
                    <br />
                    Then, enter whether you want this number to increase or decrease. For example,
                    you’d want “walks around the block” to increase, but “resting heart rate” to
                    decrease.
                  </p>
                </Stack>
              }
            >
              <span className={clsx('material-icons', style.infoIcon)}>info</span>
            </Tooltip>
          </Stack>
          <Stack width="100%" display="flex" flexDirection="row" alignItems="center" gap="8px">
            <Stack width="100%" maxWidth="160px">
              <Dropdown
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={style.dropdown}
                data={metricList}
                label="Select"
                name="direction"
                id="direction"
                value={formik.values.direction?.toString() ?? ''}
              />
              <ErrorContainer
                visible={formik.errors.direction && formik.touched.direction}
                name="direction"
                errors={formik.errors}
              />
            </Stack>
            <Stack width="100%">
              <InputField
                classnamesProps={style.inputField}
                id="name"
                name="name"
                placeholder="Example: calls to sister"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name || ''}
              />
              <ErrorContainer
                visible={formik.errors.name && formik.touched.name}
                name="name"
                errors={formik.errors}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
      <Stack alignItems="end">
        <Button
          disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
          type="submit"
          text="Confirm"
          theme="primary"
          customStyleClass={style.confirmButton}
        />
      </Stack>
    </form>
  );
};

export default HealthTrackerForm;
